import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Section, EmptyState } from "../../components/Common";
import { toast } from "sonner";
import { Plus, ClipboardList, Trash2, Pencil, X, Users } from "lucide-react";
import { useDataSync } from "../../lib/realtime";
import TeacherAttemptsDrawer from "./TeacherAttemptsDrawer";

const blankQuestion = () => ({ text: "", options: ["", "", "", ""], correct_index: 0, points: 1 });

export default function TeacherTests() {
  const [list, setList] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [evaluating, setEvaluating] = useState(null);
  const [form, setForm] = useState({ batch_id: "", title: "", description: "", duration_minutes: 30, questions: [blankQuestion()] });

  const load = async () => {
    const [t, b] = await Promise.all([api.get("/tests"), api.get("/teacher/batches")]);
    setList(t.data);
    setBatches(b.data);
  };
  useEffect(() => { load(); }, []);
  useDataSync("tests", () => load());

  const updateQ = (i, key, val) => {
    setForm((f) => {
      const qs = [...f.questions];
      qs[i] = { ...qs[i], [key]: val };
      return { ...f, questions: qs };
    });
  };

  const updateOpt = (i, j, val) => {
    setForm((f) => {
      const qs = [...f.questions];
      const opts = [...qs[i].options];
      opts[j] = val;
      qs[i] = { ...qs[i], options: opts };
      return { ...f, questions: qs };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, duration_minutes: parseInt(form.duration_minutes, 10) };
      if (editing) {
        await api.patch(`/teacher/tests/${editing.id}`, payload);
        toast.success("Test updated — student submissions preserved");
      } else {
        await api.post("/teacher/tests", payload);
        toast.success("Test created");
      }
      setForm({ batch_id: "", title: "", description: "", duration_minutes: 30, questions: [blankQuestion()] });
      setShowForm(false); setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEdit = async (test) => {
    try {
      const { data } = await api.get(`/tests/${test.id}`);
      setEditing(data);
      setForm({
        batch_id: data.batch_id,
        title: data.title,
        description: data.description || "",
        duration_minutes: data.duration_minutes,
        questions: (data.questions || []).map((q) => ({
          text: q.text,
          options: [...q.options],
          correct_index: q.correct_index,
          points: q.points || 1,
        })),
      });
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeTest = async (test) => {
    if (!confirm(`Delete "${test.title}"? Student submissions will be preserved.`)) return;
    try {
      await api.delete(`/teacher/tests/${test.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Tests & Quizzes"
        subtitle="Build MCQ tests with auto-grading."
        actions={
          <button onClick={() => { setShowForm((v) => !v); setEditing(null); setForm({ batch_id: "", title: "", description: "", duration_minutes: 30, questions: [blankQuestion()] }); }}
            data-testid="btn-add-test"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Create test
          </button>
        }
      />

      {showForm && (
        <Section title={editing ? `Edit: ${editing.title}` : "New test"} actions={
          <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        }>
          <form onSubmit={submit} className="space-y-5" data-testid="test-form">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">Batch</label>
                <select required value={form.batch_id} onChange={(e) => setForm({ ...form, batch_id: e.target.value })}
                  data-testid="test-batch" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none">
                  <option value="">— select —</option>
                  {batches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-semibold text-slate-700">Title</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="test-title" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Duration (min)</label>
                <input type="number" min="1" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                  data-testid="test-duration" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-semibold text-slate-700">Description</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
              </div>
            </div>

            <div className="space-y-4">
              {form.questions.map((q, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Question {i + 1}</span>
                    {form.questions.length > 1 && (
                      <button type="button" onClick={() => setForm({ ...form, questions: form.questions.filter((_, idx) => idx !== i) })}
                        className="text-rose-500 hover:bg-rose-50 p-1 rounded">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <input required placeholder="Question text" value={q.text} onChange={(e) => updateQ(i, "text", e.target.value)}
                    data-testid={`q-${i}-text`}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {q.options.map((opt, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <input type="radio" name={`q-${i}-correct`} checked={q.correct_index === j} onChange={() => updateQ(i, "correct_index", j)}
                          data-testid={`q-${i}-correct-${j}`} />
                        <input required placeholder={`Option ${j + 1}`} value={opt} onChange={(e) => updateOpt(i, j, e.target.value)}
                          data-testid={`q-${i}-opt-${j}`}
                          className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none text-sm" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setForm({ ...form, questions: [...form.questions, blankQuestion()] })}
                data-testid="add-question"
                className="px-4 py-2 rounded-lg border border-dashed border-blue-300 text-blue-600 text-sm font-semibold hover:bg-blue-50">
                + Add question
              </button>
            </div>

            <div className="flex gap-2">
              <button type="submit" data-testid="test-submit" className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">Create test</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </Section>
      )}

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((t) => {
          const batch = batches.find((b) => b.id === t.batch_id);
          return (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-5 ym-card-hover">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center">
                  <ClipboardList className="h-5 w-5" />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEvaluating(t)} className="text-emerald-600 hover:bg-emerald-50 p-1.5 rounded-lg" data-testid={`evaluate-test-${t.id}`} title="View attempts">
                    <Users className="h-4 w-4" />
                  </button>
                  <button onClick={() => startEdit(t)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg" data-testid={`edit-test-${t.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => removeTest(t)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded-lg" data-testid={`delete-test-${t.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <h3 className="mt-4 font-heading font-bold text-slate-900">{t.title}</h3>
              {batch && <p className="text-xs text-slate-500">{batch.name}</p>}
              <div className="mt-3 flex gap-3 text-xs text-slate-500">
                <span>{t.questions.length} Qs</span>
                <span>{t.duration_minutes} min</span>
                {t.version > 1 && <span className="text-slate-400">v{t.version}</span>}
              </div>
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3">
            <EmptyState title="No tests yet" desc="Create your first MCQ test." icon={ClipboardList} />
          </div>
        )}
      </div>

      {evaluating && (
        <TeacherAttemptsDrawer test={evaluating} onClose={() => setEvaluating(null)} />
      )}
    </div>
  );
}
